// ---------------------------------
// client/src/pages/Home.jsx
// Landing page with open-jobs snapshot and shared calendar board
// ---------------------------------
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import jwt_decode from 'jwt-decode';
import { fetchCurrentJobs } from '../utils/jobs';
import { computeJobProgress, JOB_STATUS_LABELS } from '../utils/jobProgress';
import { formatDate } from '../utils/date';
import { fetchUserDirectory } from '../utils/users';
import {
    fetchCalendarEntries,
    upsertCalendarEntry,
} from '../utils/calendar';

const TEXT = {
    title: 'ברוכים הבאים ל- לאור הנדסה בע"מ',
    subtitle: 'ניהול עבודות, טפסי PDF ויומן תפעולי במקום אחד.',
    startJobs: 'לוח עבודות',
    adminJobTypes: 'ניהול סוגי עבודה',
    adminTemplates: 'תבניות PDF',
    login: 'כניסה',
    signup: 'הרשמה',
    openJobsTitle: 'עבודות פתוחות כעת',
    openJobsHint: 'לחצו על כרטיס עבודה כדי לעבור לפרטים.',
    openJobsError: 'לא ניתן לטעון את העבודות הפתוחות כרגע.',
    openJobsLoading: 'טוען עבודות פתוחות...',
    openJobsEmpty: 'אין עבודות פתוחות כרגע.',
    openJobsUpdated: 'עודכן לאחרונה',
    openJobsNoDate: 'לא עודכן',
    openJobsNoType: 'ללא סוג עבודה',
    openJobsNoNumber: 'ללא מספר עבודה',
    calendarTitle: 'לוח הוראות יומי',
    calendarSubtitle: 'לחיצה על תאריך מוסיפה או מעדכנת הוראות ומשמרת עבודה.',
    calendarLoading: 'טוען יומן...',
    calendarError: 'לא ניתן לטעון את היומן.',
    calendarPrevWeek: 'שבוע קודם',
    calendarNextWeek: 'שבוע הבא',
    calendarPickDate: 'בחר תאריך',
    calendarToday: 'היום',
    calendarEmpty: 'ללא הוראות',
    calendarDone: 'בוצע',
    calendarModalTitle: 'עדכון יום עבודה',
    calendarDate: 'תאריך',
    calendarInstructions: 'הוראות יומיות',
    calendarWorkingTeam: 'צוות עבודה',
    calendarWorkingTeamHint: 'ניתן להקליד ידנית ולהפריד בין שמות עובדים בפסיק.',
    calendarMarkDone: 'סמן כיום שבוצע',
    calendarSave: 'שמור',
    calendarDelete: 'מחק',
    calendarCancel: 'ביטול',
    calendarSaving: 'שומר...',
    calendarDeleteConfirm: 'למחוק את ההוראות ליום זה?',
    calendarSaveError: 'לא ניתן לשמור כרגע.',
    calendarDeleteError: 'לא ניתן למחוק כרגע.',
    modalEmptyDate: 'בחר יום בלוח',
};

TEXT.title = 'לאור הנדסה ואחזקה תעשייתית בע"מ';
TEXT.subtitle = 'ניהול עבודות, טפסי PDF ולוח משימות יומי במקום אחד.';
TEXT.todayTitle = 'המשימות של היום';
TEXT.todayHint = 'תצוגה מהירה לעובדים לפני שנכנסים ללוח השבועי.';
TEXT.todayOpen = 'פתח את יום העבודה';
TEXT.todayEmpty = 'אין כרגע הוראות ליום זה.';
TEXT.todayLoading = 'טוען את משימות היום...';
TEXT.todayWorkers = 'צוות היום';

const STATUS_CLASS = {
    in_progress: 'status-badge--info',
    not_started: 'status-badge--warning',
};

const WEEKDAY_LABELS = ['א', 'ב', 'ג', 'ד', 'ה', 'ו', 'ש'];

function formatDateKey(date) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
}

function getWeekStart(date) {
    const target = new Date(date);
    target.setHours(0, 0, 0, 0);
    const dayOffset = target.getDay();
    target.setDate(target.getDate() - dayOffset);
    return target;
}

function getWeekDates(anchorDate) {
    const weekStart = getWeekStart(anchorDate);
    const dates = [];
    for (let i = 0; i < 7; i += 1) {
        const current = new Date(weekStart);
        current.setDate(weekStart.getDate() + i);
        dates.push(current);
    }
    return dates;
}

function getWeekRange(anchorDate) {
    const first = getWeekStart(anchorDate);
    const last = new Date(first);
    last.setDate(first.getDate() + 6);
    return {
        from: formatDateKey(first),
        to: formatDateKey(last),
    };
}

const WEEK_TITLE_FORMATTER = new Intl.DateTimeFormat('he-IL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
});

function formatWeekRangeDate(date) {
    return WEEK_TITLE_FORMATTER.format(date);
}

function formatWeekTitle(anchorDate) {
    const weekStart = getWeekStart(anchorDate);
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 6);
    return `${formatWeekRangeDate(weekStart)} - ${formatWeekRangeDate(weekEnd)}`;
}

function parseWorkingTeamEntries(value) {
    return String(value || '')
        .split(',')
        .map((entry) => entry.trim())
        .filter(Boolean);
}

function stringifyWorkingTeamEntries(entries) {
    const normalized = entries
        .map((entry) => String(entry || '').trim())
        .filter(Boolean);
    return normalized.length > 0 ? `${normalized.join(', ')}, ` : '';
}

export default function Home() {
    const token = localStorage.getItem('token');
    const isAuthenticated = Boolean(token);
    let isAdmin = false;
    if (token) {
        try {
            const { role } = jwt_decode(token);
            isAdmin = role === 'admin';
        } catch {
            isAdmin = false;
        }
    }

    const [jobs, setJobs] = useState([]);
    const [jobsError, setJobsError] = useState('');
    const [isLoadingJobs, setIsLoadingJobs] = useState(false);

    const [weekAnchorDate, setWeekAnchorDate] = useState(() => new Date());
    const [calendarEntries, setCalendarEntries] = useState({});
    const [todayEntry, setTodayEntry] = useState(null);
    const [calendarError, setCalendarError] = useState('');
    const [isCalendarLoading, setIsCalendarLoading] = useState(false);
    const [isTodayLoading, setIsTodayLoading] = useState(false);
    const [selectedDate, setSelectedDate] = useState('');
    const [isCalendarModalOpen, setIsCalendarModalOpen] = useState(false);
    const [userDirectory, setUserDirectory] = useState([]);
    const [isTeamPickerOpen, setIsTeamPickerOpen] = useState(false);
    const [calendarForm, setCalendarForm] = useState({
        instructions: '',
        workingTeam: '',
        isDone: false,
    });
    const [isCalendarSaving, setIsCalendarSaving] = useState(false);
    const teamPickerRef = useRef(null);

    useEffect(() => {
        if (!isAuthenticated) {
            return;
        }
        let isMounted = true;
        const load = async () => {
            try {
                setIsLoadingJobs(true);
                setJobsError('');
                const nextJobs = await fetchCurrentJobs();
                if (!isMounted) return;
                setJobs(nextJobs);
            } catch (err) {
                if (isMounted) {
                    setJobsError(err.response?.data?.message || TEXT.openJobsError);
                }
            } finally {
                if (isMounted) {
                    setIsLoadingJobs(false);
                }
            }
        };
        load();
        return () => {
            isMounted = false;
        };
    }, [isAuthenticated]);

    useEffect(() => {
        if (!isAuthenticated) return;
        let isMounted = true;
        const loadCalendar = async () => {
            try {
                setIsCalendarLoading(true);
                setCalendarError('');
                const range = getWeekRange(weekAnchorDate);
                const data = await fetchCalendarEntries(range);
                if (!isMounted) return;
                const nextMap = {};
                data.forEach((entry) => {
                    if (!entry?.date) return;
                    nextMap[entry.date] = entry;
                });
                setCalendarEntries(nextMap);
            } catch (err) {
                if (isMounted) {
                    setCalendarError(err.response?.data?.message || TEXT.calendarError);
                }
            } finally {
                if (isMounted) {
                    setIsCalendarLoading(false);
                }
            }
        };
        loadCalendar();
        return () => {
            isMounted = false;
        };
    }, [weekAnchorDate, isAuthenticated]);

    useEffect(() => {
        if (!isAuthenticated) {
            setTodayEntry(null);
            return undefined;
        }
        let isMounted = true;
        const today = formatDateKey(new Date());
        const loadToday = async () => {
            try {
                setIsTodayLoading(true);
                const data = await fetchCalendarEntries({ from: today, to: today });
                if (!isMounted) return;
                setTodayEntry(Array.isArray(data) && data.length > 0 ? data[0] : null);
            } catch {
                if (!isMounted) return;
                setTodayEntry(null);
            } finally {
                if (isMounted) {
                    setIsTodayLoading(false);
                }
            }
        };
        loadToday();
        return () => {
            isMounted = false;
        };
    }, [isAuthenticated]);

    useEffect(() => {
        if (!isAuthenticated) {
            setUserDirectory([]);
            return;
        }
        let isMounted = true;
        fetchUserDirectory()
            .then((data) => {
                if (!isMounted) return;
                setUserDirectory(Array.isArray(data) ? data : []);
            })
            .catch(() => {
                if (!isMounted) return;
                setUserDirectory([]);
            });
        return () => {
            isMounted = false;
        };
    }, [isAuthenticated]);

    useEffect(() => {
        if (!isTeamPickerOpen) return undefined;
        const handlePointerDown = (event) => {
            if (!teamPickerRef.current?.contains(event.target)) {
                setIsTeamPickerOpen(false);
            }
        };
        document.addEventListener('mousedown', handlePointerDown);
        return () => document.removeEventListener('mousedown', handlePointerDown);
    }, [isTeamPickerOpen]);

    const openJobs = useMemo(() => {
        const rows = (Array.isArray(jobs) ? jobs : []).map((job) => computeJobProgress(job));
        return rows
            .filter((job) => job.status === 'in_progress' || job.status === 'not_started')
            .sort((a, b) => b.lastActivity - a.lastActivity);
    }, [jobs]);

    const weekDates = useMemo(() => getWeekDates(weekAnchorDate), [weekAnchorDate]);
    const weekTitle = useMemo(() => formatWeekTitle(weekAnchorDate), [weekAnchorDate]);
    const todayKey = formatDateKey(new Date());
    const todayPreviewEntry = todayEntry || calendarEntries[todayKey] || null;
    const selectedWorkingTeamNames = useMemo(
        () => new Set(parseWorkingTeamEntries(calendarForm.workingTeam)),
        [calendarForm.workingTeam],
    );

    const openCalendarEditor = (dateKey) => {
        const entry = calendarEntries[dateKey];
        setSelectedDate(dateKey);
        setCalendarForm({
            instructions: entry?.instructions || '',
            workingTeam: entry?.workingTeam || '',
            isDone: Boolean(entry?.isDone),
        });
        setCalendarError('');
        setIsTeamPickerOpen(false);
        setIsCalendarModalOpen(true);
    };

    const closeCalendarEditor = () => {
        if (isCalendarSaving) return;
        setIsTeamPickerOpen(false);
        setIsCalendarModalOpen(false);
    };

    const handleToggleWorkingTeamUser = (userName) => {
        setCalendarForm((prev) => {
            const entries = parseWorkingTeamEntries(prev.workingTeam);
            const hasEntry = entries.includes(userName);
            const nextEntries = hasEntry
                ? entries.filter((entry) => entry !== userName)
                : [...entries, userName];
            return {
                ...prev,
                workingTeam: stringifyWorkingTeamEntries(nextEntries),
            };
        });
    };

    const handleSaveCalendarEntry = async (event) => {
        event.preventDefault();
        if (!selectedDate) return;
        try {
            setIsCalendarSaving(true);
            setCalendarError('');
            const saved = await upsertCalendarEntry(selectedDate, calendarForm);
            setCalendarEntries((prev) => {
                const next = { ...prev };
                if (saved?.deleted) {
                    delete next[selectedDate];
                    return next;
                }
                next[selectedDate] = saved;
                return next;
            });
            if (selectedDate === todayKey) {
                setTodayEntry(saved?.deleted ? null : saved);
            }
            setIsCalendarModalOpen(false);
        } catch (err) {
            setCalendarError(err.response?.data?.message || TEXT.calendarSaveError);
        } finally {
            setIsCalendarSaving(false);
        }
    };

    return (
        <div className="page page--home stack stack--lg">
            <section className="home-hero">
                <div className="home-hero__heading">
                    <span className="home-hero__eyebrow">לאור הנדסה בע"מ</span>
                    <h1 className="home-hero__title">{TEXT.title}</h1>
                    <p className="home-hero__subtitle">{TEXT.subtitle}</p>
                </div>
                <div className="home-hero__actions">
                    {isAuthenticated ? (
                        <Link className="button button--primary home-hero__action" to="/dashboard">
                            {TEXT.startJobs}
                        </Link>
                    ) : (
                        <>
                            <Link className="button button--primary home-hero__action" to="/login">
                                {TEXT.login}
                            </Link>
                            <Link className="button button--light home-hero__action" to="/signup">
                                {TEXT.signup}
                            </Link>
                        </>
                    )}
                    {isAuthenticated && (
                        <Link className="button button--light home-hero__action" to="/instructions-manual">
                        הוראות
                        </Link>
                    )}
                    {isAuthenticated && (
                        <Link className="button button--light home-hero__action" to="/contact-us">
                            צור קשר
                        </Link>
                    )}
                    {isAdmin && (
                        <>
                            <Link className="button button--light home-hero__action" to="/admin/job-types">
                                {TEXT.adminJobTypes}
                            </Link>
                            <Link className="button button--light home-hero__action" to="/admin/pdf-templates">
                                {TEXT.adminTemplates}
                            </Link>
                        </>
                    )}
                </div>
            </section>

            {isAuthenticated && (
                <section className="card home-today">
                    <div className="home-today__header">
                        <div className="stack">
                            <h2 className="card__title">{TEXT.todayTitle}</h2>
                            <p className="card__meta">{TEXT.todayHint}</p>
                        </div>
                        <button
                            type="button"
                            className="button button--primary"
                            onClick={() => openCalendarEditor(todayKey)}
                        >
                            {TEXT.todayOpen}
                        </button>
                    </div>

                    {isTodayLoading ? (
                        <div className="card__meta">{TEXT.todayLoading}</div>
                    ) : todayPreviewEntry ? (
                        <div className="home-today__body">
                            <div className="home-today__instructions">
                                {todayPreviewEntry.instructions || TEXT.todayEmpty}
                            </div>
                            {parseWorkingTeamEntries(todayPreviewEntry.workingTeam).length > 0 && (
                                <div className="stack stack--sm">
                                    <span className="card__meta">{TEXT.todayWorkers}</span>
                                    <div className="home-worker-chips">
                                        {parseWorkingTeamEntries(todayPreviewEntry.workingTeam).map((name) => (
                                            <span key={name} className="chip">
                                                {name}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {todayPreviewEntry.isDone && (
                                <span className="status-badge status-badge--success">{TEXT.calendarDone}</span>
                            )}
                        </div>
                    ) : (
                        <div className="card__meta">{TEXT.todayEmpty}</div>
                    )}
                </section>
            )}

            {isAuthenticated && (
                <section className="card home-calendar">
                    <div className="home-calendar__header">
                        <div className="stack">
                            <h2 className="card__title">{TEXT.calendarTitle}</h2>
                            <p className="card__meta">{TEXT.calendarSubtitle}</p>
                        </div>
                        <div className="home-calendar__month-controls">
                            <button
                                type="button"
                                className="button button--light"
                                onClick={() =>
                                    setWeekAnchorDate((prev) => {
                                        const next = new Date(prev);
                                        next.setDate(next.getDate() - 7);
                                        return next;
                                    })
                                }
                            >
                                {TEXT.calendarPrevWeek}
                            </button>
                            <strong className="home-calendar__week-title">{weekTitle}</strong>
                            <button
                                type="button"
                                className="button button--light"
                                onClick={() =>
                                    setWeekAnchorDate((prev) => {
                                        const next = new Date(prev);
                                        next.setDate(next.getDate() + 7);
                                        return next;
                                    })
                                }
                            >
                                {TEXT.calendarNextWeek}
                            </button>
                            <input
                                className="home-calendar__date-input"
                                type="date"
                                value={formatDateKey(weekAnchorDate)}
                                onChange={(event) => {
                                    const next = new Date(`${event.target.value}T12:00:00`);
                                    if (!Number.isNaN(next.getTime())) {
                                        setWeekAnchorDate(next);
                                    }
                                }}
                                aria-label={TEXT.calendarPickDate}
                            />
                            <button
                                type="button"
                                className="button button--ghost"
                                onClick={() => {
                                    setWeekAnchorDate(new Date());
                                }}
                            >
                                {TEXT.calendarToday}
                            </button>
                        </div>
                    </div>

                    {calendarError && <div className="alert alert--danger">{calendarError}</div>}
                    {isCalendarLoading ? (
                        <div className="card__meta">{TEXT.calendarLoading}</div>
                    ) : (
                        <div className="home-calendar__grid-wrap">
                            <div className="home-calendar__weekdays">
                                {WEEKDAY_LABELS.map((label) => (
                                    <span key={label}>{label}</span>
                                ))}
                            </div>
                            <div className="home-calendar__grid">
                                {weekDates.map((date) => {
                                    const dateKey = formatDateKey(date);
                                    const entry = calendarEntries[dateKey];
                                    const isToday = dateKey === todayKey;
                                    const classes = [
                                        'home-calendar__day',
                                        entry?.isDone
                                            ? 'home-calendar__day--done'
                                            : isToday
                                                ? 'home-calendar__day--today'
                                                : '',
                                    ]
                                        .filter(Boolean)
                                        .join(' ');
                                    return (
                                        <button
                                            type="button"
                                            key={dateKey}
                                            className={classes}
                                            onClick={() => openCalendarEditor(dateKey)}
                                        >
                                            <span className="home-calendar__day-number">{date.getDate()}</span>
                                            <span className="home-calendar__day-text">
                                                {entry?.instructions || TEXT.calendarEmpty}
                                            </span>
                                            {entry?.workingTeam && (
                                                <span className="home-calendar__day-team">
                                                    צוות: {entry.workingTeam}
                                                </span>
                                            )}
                                            {entry?.isDone && (
                                                <span className="home-calendar__done-chip">{TEXT.calendarDone}</span>
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </section>
            )}

            {isAuthenticated && (
                <section className="stack home-summary">
                    <div className="stack">
                        <h2 className="card__title">{TEXT.openJobsTitle}</h2>
                        <p className="card__meta">{TEXT.openJobsHint}</p>
                    </div>
                    {jobsError && <div className="alert alert--danger">{jobsError}</div>}
                    {isLoadingJobs ? (
                        <div className="card">{TEXT.openJobsLoading}</div>
                    ) : openJobs.length === 0 ? (
                        <div className="card">{TEXT.openJobsEmpty}</div>
                    ) : (
                        <div className="card">
                            {openJobs.map((job) => {
                                const lastUpdated =
                                    job.lastActivity > 0 ? formatDate(job.lastActivity) : TEXT.openJobsNoDate;
                                return (
                                    <Link
                                        key={job.jobId ?? job.jobNumber}
                                        className="job-highlight job-highlight--link"
                                        to={`/jobs/${job.jobId}`}
                                    >
                                        <div className="job-highlight__header">
                                            <span className="job-highlight__title">
                                                {job.jobNumber || TEXT.openJobsNoNumber}
                                            </span>
                                            <span
                                                className={`status-badge ${
                                                    STATUS_CLASS[job.status] || 'status-badge--info'
                                                }`}
                                            >
                                                {JOB_STATUS_LABELS[job.status] || job.status}
                                            </span>
                                        </div>
                                        <div className="job-highlight__meta">
                                            <span>{job.jobTypeName || TEXT.openJobsNoType}</span>
                                            <span>
                                                {TEXT.openJobsUpdated}: {lastUpdated}
                                            </span>
                                        </div>
                                    </Link>
                                );
                            })}
                        </div>
                    )}
                </section>
            )}

            {isCalendarModalOpen && (
                <div
                    className="modal-overlay"
                    role="dialog"
                    aria-modal="true"
                    onClick={(event) => {
                        if (event.target === event.currentTarget) {
                            closeCalendarEditor();
                        }
                    }}
                >
                    <div className="modal-content" role="document">
                        <header className="stack">
                            <h2 className="card__title">{TEXT.calendarModalTitle}</h2>
                            <p className="card__meta">
                                {TEXT.calendarDate}: {selectedDate || TEXT.modalEmptyDate}
                            </p>
                        </header>
                        <form className="modal-form" onSubmit={handleSaveCalendarEntry}>
                            <div className="modal-form__body">
                                <label className="form-field">
                                    <span className="form-field__label">{TEXT.calendarInstructions}</span>
                                    <textarea
                                        className="form-field__control form-field__control--textarea"
                                        rows={5}
                                        value={calendarForm.instructions}
                                        onChange={(event) =>
                                            setCalendarForm((prev) => ({
                                                ...prev,
                                                instructions: event.target.value,
                                            }))
                                        }
                                    />
                                </label>
                                <label className="form-field">
                                    <span className="form-field__label">{TEXT.calendarWorkingTeam}</span>
                                    <span className="form-field__description">{TEXT.calendarWorkingTeamHint}</span>
                                    <div className="team-selector" ref={teamPickerRef}>
                                        <input
                                            className="form-field__control"
                                            type="text"
                                            value={calendarForm.workingTeam}
                                            onChange={(event) =>
                                                setCalendarForm((prev) => ({
                                                    ...prev,
                                                    workingTeam: event.target.value,
                                                }))
                                            }
                                        />
                                        <button
                                            type="button"
                                            className="button button--light team-selector__toggle"
                                            onClick={() => setIsTeamPickerOpen((prev) => !prev)}
                                            aria-expanded={isTeamPickerOpen}
                                        >
                                            {'בחר משתמשים'}
                                        </button>
                                        {isTeamPickerOpen && (
                                            <div
                                                className="team-selector__menu"
                                                role="group"
                                                aria-label={'רשימת משתמשים'}
                                            >
                                                {userDirectory.length === 0 ? (
                                                    <div className="card__meta">{'אין משתמשים להצגה.'}</div>
                                                ) : (
                                                    userDirectory.map((user) => {
                                                        const isSelected = selectedWorkingTeamNames.has(user.name);
                                                        return (
                                                            <label
                                                                key={user.id}
                                                                className={`team-selector__option${isSelected ? ' team-selector__option--selected' : ''}`}
                                                            >
                                                                <input
                                                                    type="checkbox"
                                                                    checked={isSelected}
                                                                    onChange={() => handleToggleWorkingTeamUser(user.name)}
                                                                />
                                                                <span>{user.name}</span>
                                                            </label>
                                                        );
                                                    })
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </label>
                                <label className="check-toggle home-calendar__done-toggle">
                                    <input
                                        type="checkbox"
                                        checked={calendarForm.isDone}
                                        onChange={(event) =>
                                            setCalendarForm((prev) => ({
                                                ...prev,
                                                isDone: event.target.checked,
                                            }))
                                        }
                                    />
                                    <span>{TEXT.calendarMarkDone}</span>
                                </label>
                            </div>
                            <footer className="modal-form__footer">
                                <button
                                    type="button"
                                    className="button button--ghost"
                                    onClick={closeCalendarEditor}
                                    disabled={isCalendarSaving}
                                >
                                    {TEXT.calendarCancel}
                                </button>
                                <button
                                    type="submit"
                                    className="button button--primary"
                                    disabled={isCalendarSaving}
                                >
                                    {isCalendarSaving ? TEXT.calendarSaving : TEXT.calendarSave}
                                </button>
                            </footer>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
